import React, { useState } from 'react';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Settings, Database, Activity, Home, User, Link, Calendar, Share2, Zap, ImageIcon, AlertTriangle, Video, MessageCircle, Cog, Mail, Shield, Bot, Key } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import AdminUserManagement from '@/components/AdminUserManagement';
import AdminModelManagement from '@/components/AdminModelManagement';
import AdminAPIKeyManagement from '@/components/AdminAPIKeyManagement';
import AdminSocialMediaManagement from '@/components/AdminSocialMediaManagement';
import AdminImageSharing from '@/components/AdminImageSharing';
import AdminA4FModelManagement from '@/components/AdminA4FModelManagement';
import AdminIndustryImages from '@/components/AdminIndustryImages';
import AdminVideoManagement from '@/components/AdminVideoManagement';
import AdminForumManagement from '@/components/AdminForumManagement';
import AdminSiteConfiguration from '@/components/AdminSiteConfiguration';
import AdminEmailServiceManagement from '@/components/AdminEmailServiceManagement';
import AdminLogoManagement from '@/components/AdminLogoManagement';
import AdminAccessManagement from '@/components/AdminAccessManagement';
import AdminEmailAllowlist from '@/components/AdminEmailAllowlist';
import SecurityDashboard from '@/components/SecurityDashboard';
import { SecurityValidation } from '@/components/SecurityValidation';
import { AdminAutomationAgent } from '@/components/AdminAutomationAgent';
import { AdminSiteConfigLimitsSettings } from '@/components/AdminSiteConfigLimitsSettings';
import { useAdminStats } from '@/hooks/useAdminStats';

const AdminPanel = () => {
  const {
    user,
    signOut
  } = useSecureAuthContext();
  const [activeTab, setActiveTab] = useState('users');
  const {
    stats,
    loading: statsLoading,
    error: statsError
  } = useAdminStats();
  const handleSignOut = async () => {
    await signOut();
  };
  const statsData = [{
    title: "Total Users",
    value: stats.totalUsers,
    change: "+12% this month",
    icon: Users,
    color: "text-blue-400"
  }, {
    title: "Active Sessions",
    value: stats.activeSessions,
    change: "+5% today",
    icon: Activity,
    color: "text-green-400"
  }, {
    title: "API Calls",
    value: stats.apiCalls,
    change: "+18% this week",
    icon: Database,
    color: "text-purple-400"
  }, {
    title: "System Health",
    value: stats.systemHealth,
    change: "Excellent",
    icon: Settings,
    color: "text-yellow-400"
  }];
  const renderTabContent = () => {
    switch (activeTab) {
      case 'users':
        return <AdminUserManagement />;
      case 'models':
        return <AdminModelManagement />;
      case 'api-keys':
        return <AdminAPIKeyManagement />;
      case 'prompt-enhancement':
        return <AdminA4FModelManagement />;
      case 'social':
        return <AdminSocialMediaManagement />;
      case 'sharing':
        return <AdminImageSharing />;
      case 'industry-images':
        return <AdminIndustryImages />;
      case 'videos':
        return <AdminVideoManagement />;
      case 'admin-access':
        return <AdminAccessManagement />;
      case 'email-allowlist':
        return <AdminEmailAllowlist />;
      case 'security':
        return <div className="space-y-6">
            <SecurityDashboard />
            <SecurityValidation />
          </div>;
      case 'forum':
        return <AdminForumManagement />;
      case 'site-config':
        return <AdminSiteConfiguration />;
      case 'email-services':
        return <AdminEmailServiceManagement />;
      case 'logo-management':
        return <AdminLogoManagement />;
      case 'automation':
        return <AdminAutomationAgent />;
      case 'limits':
        return <AdminSiteConfigLimitsSettings />;
      default:
        return <div className="text-white">Select a tab to view content</div>;
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10"></div>
      </div>

      {/* Floating orbs */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-cyan-500/30 rounded-full blur-2xl animate-bounce"></div>

      <header className="bg-black/20 backdrop-blur-md border-b border-white/10 relative z-10 shadow-lg">
        <div className="container mx-auto px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4">
          <nav className="flex items-center justify-between">
            <RouterLink to="/" className="flex items-center space-x-1.5 sm:space-x-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-xs sm:text-sm">BG</span>
              </div>
              <span className="text-sm sm:text-lg md:text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Admin<span className="hidden sm:inline"> Panel</span>
              </span>
            </RouterLink>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-3 lg:space-x-4">
              <RouterLink to="/">
                <Button variant="ghost" className="flex items-center text-white hover:bg-white/10 border border-white/20 hover:border-white/30 h-9">
                  <Home className="w-4 h-4 mr-2" />
                  Home
                </Button>
              </RouterLink>
              <RouterLink to="/dashboard">
                <Button variant="ghost" className="flex items-center text-white hover:bg-white/10 border border-white/20 hover:border-white/30 h-9">
                  <Settings className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </RouterLink>
              <div className="flex items-center space-x-2 px-3 py-2 bg-white/10 rounded-lg border border-white/10 backdrop-blur-sm">
                <User className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-white max-w-[150px] truncate">{user?.email}</span>
              </div>
              <Button variant="ghost" className="text-white hover:bg-white/10 border border-white/20 hover:border-white/30 h-9" onClick={handleSignOut}>
                Sign Out
              </Button>
            </div>

            {/* Mobile Navigation */}
            <div className="flex md:hidden items-center space-x-2">
              <RouterLink to="/dashboard">
                <Button variant="ghost" size="sm" className="text-white hover:bg-white/10 border border-white/20 p-2 h-9 w-9">
                  <Settings className="w-4 h-4" />
                </Button>
              </RouterLink>
              <Button variant="ghost" size="sm" className="text-red-400 hover:bg-white/10 border border-white/20 hover:border-red-400/30 h-9 px-3 text-xs" onClick={handleSignOut}>
                Sign Out
              </Button>
            </div>
          </nav>
        </div>
      </header>

      <main className="container mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 lg:py-12 relative z-10">
        {/* Quick Access Buttons - Top Right */}
        <div className="absolute top-4 right-6 z-50 pointer-events-auto flex gap-2">
          <Button 
            onClick={() => setActiveTab('api-keys')} 
            className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 pointer-events-auto" 
            size="sm"
          >
            <Key className="w-4 h-4 mr-2" />
            API Keys
          </Button>
          <Button 
            onClick={() => setActiveTab('automation')} 
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 pointer-events-auto" 
            size="sm"
          >
            <Bot className="w-4 h-4 mr-2" />
            Automation
          </Button>
        </div>

        <div className="mb-4 sm:mb-6 md:mb-8">
          <Badge className="mb-2 sm:mb-3 md:mb-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-300 border-blue-500/30 text-xs px-2 py-1">
            Admin Panel
          </Badge>
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-1 sm:mb-2">
            System Administration
          </h1>
          <p className="text-xs sm:text-sm md:text-base text-gray-300">Manage users, models, social media and system settings</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8 md:mb-12">
          {statsData.map((stat, index) => <Card key={index} className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all duration-300">
              <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xs sm:text-sm font-medium text-gray-300">{stat.title}</CardTitle>
                  <stat.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-1">
                  {statsLoading ? <div className="animate-pulse bg-gray-600 h-5 sm:h-6 w-12 sm:w-16 rounded"></div> : stat.value}
                </div>
                {statsError ? <div className="flex items-center text-[10px] sm:text-xs text-red-400">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Error loading
                  </div> : <p className="text-xs sm:text-sm text-green-400">{stat.change}</p>}
              </CardContent>
            </Card>)}
        </div>

        {/* Navigation Tabs */}
        <div className="mb-4 sm:mb-6 md:mb-8">
          <div className="flex space-x-2 sm:space-x-3 md:space-x-4 border-b border-white/20 overflow-x-auto pb-px scrollbar-thin scrollbar-thumb-white/20">
            <button onClick={() => setActiveTab('users')} className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1.5 sm:space-x-2 ${activeTab === 'users' ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">User Management</span>
              <span className="xs:hidden">Users</span>
            </button>
            <button onClick={() => setActiveTab('email-services')} className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1.5 sm:space-x-2 ${activeTab === 'email-services' ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Email</span>
            </button>
            <button onClick={() => setActiveTab('logo-management')} className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1.5 sm:space-x-2 ${activeTab === 'logo-management' ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <ImageIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Logo</span>
            </button>
            <button onClick={() => setActiveTab('site-config')} className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1.5 sm:space-x-2 ${activeTab === 'site-config' ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Cog className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Config</span>
            </button>
            <button onClick={() => setActiveTab('models')} className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1.5 sm:space-x-2 ${activeTab === 'models' ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Database className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Models</span>
            </button>
            <button onClick={() => setActiveTab('prompt-enhancement')} className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1.5 sm:space-x-2 ${activeTab === 'prompt-enhancement' ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Prompts</span>
            </button>
            <button onClick={() => setActiveTab('social')} className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1.5 sm:space-x-2 ${activeTab === 'social' ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Link className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Social</span>
            </button>
            <button onClick={() => setActiveTab('sharing')} className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1.5 sm:space-x-2 ${activeTab === 'sharing' ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Sharing</span>
            </button>
            <button onClick={() => setActiveTab('industry-images')} className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1.5 sm:space-x-2 ${activeTab === 'industry-images' ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <ImageIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Industry</span>
            </button>
            <button onClick={() => setActiveTab('videos')} className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1.5 sm:space-x-2 ${activeTab === 'videos' ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Video className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Videos</span>
            </button>
            <button onClick={() => setActiveTab('admin-access')} className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1.5 sm:space-x-2 ${activeTab === 'admin-access' ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Admin Access</span>
            </button>
            <button onClick={() => setActiveTab('email-allowlist')} className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1.5 sm:space-x-2 ${activeTab === 'email-allowlist' ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Email Allowlist</span>
            </button>
            <button onClick={() => setActiveTab('security')} className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1.5 sm:space-x-2 ${activeTab === 'security' ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Security</span>
            </button>
            <button onClick={() => setActiveTab('automation')} className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1.5 sm:space-x-2 ${activeTab === 'automation' ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Bot className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Automation</span>
            </button>
            <button onClick={() => setActiveTab('limits')} className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center space-x-1.5 sm:space-x-2 ${activeTab === 'limits' ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Limits</span>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-[300px] sm:min-h-[400px] md:min-h-[500px] lg:min-h-[600px]">
          {renderTabContent()}
        </div>
      </main>
    </div>;
};
export default AdminPanel;